import React from 'react';
import { Autocomplete, AutocompleteItem, Chip } from '@heroui/react';
import type { Key } from '@react-types/shared';

interface FilterStatusMultiSelectProps {
  label: string;
  value: string; // comma-separated status values
  onChange: (value: string) => void;
  className?: string;
}

const statusOptions = [
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'followup', label: 'Followup' },
  { key: 'closed', label: 'Closed' },
  { key: 'dispensable', label: 'Dispensable' }
];

export function FilterStatusMultiSelect({ label, value, onChange, className = 'w-64' }: FilterStatusMultiSelectProps) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedStatuses, setSelectedStatuses] = React.useState<string[]>([]);

  // Parse comma-separated values into array
  const selectedStatusKeys = React.useMemo(() => {
    if (!value || value.trim() === '') {
      return [];
    }
    return value
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s !== '');
  }, [value]);

  // Update selected statuses when value changes
  React.useEffect(() => {
    setSelectedStatuses(selectedStatusKeys);
  }, [selectedStatusKeys.join(',')]);

  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return statusOptions.filter((opt) => !selectedStatuses.includes(opt.key));
    }
    const term = searchTerm.toLowerCase();
    return statusOptions.filter(
      (opt) =>
        !selectedStatuses.includes(opt.key) &&
        (opt.label.toLowerCase().includes(term) || opt.key.toLowerCase().includes(term))
    );
  }, [searchTerm, selectedStatuses]);

  const handleStatusAdd = React.useCallback(
    (key: Key | null) => {
      if (!key) return;

      const statusKey = String(key);
      if (selectedStatuses.includes(statusKey)) return;

      const newSelectedStatuses = [...selectedStatuses, statusKey];
      setSelectedStatuses(newSelectedStatuses);

      // Update value with comma-separated keys
      const newValue = newSelectedStatuses.join(',');
      onChange(newValue);

      setSearchTerm('');
    },
    [selectedStatuses, onChange]
  );

  const handleStatusRemove = React.useCallback(
    (statusKey: string) => {
      const newSelectedStatuses = selectedStatuses.filter((s) => s !== statusKey);
      setSelectedStatuses(newSelectedStatuses);

      const newValue = newSelectedStatuses.join(',');
      onChange(newValue);
    },
    [selectedStatuses, onChange]
  );

  const getStatusLabel = (key: string) => {
    return statusOptions.find((opt) => opt.key === key)?.label || key;
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <label className="text-default-700 text-sm font-medium">{label}</label>
      <Autocomplete
        inputValue={searchTerm}
        onInputChange={setSearchTerm}
        selectedKey={null}
        onSelectionChange={handleStatusAdd}
        isLoading={false}
        placeholder="Search status..."
        size="sm"
        allowsCustomValue={false}
        onOpenChange={() => {}}
      >
        {filteredOptions.map((option) => (
          <AutocompleteItem key={option.key} textValue={option.label}>
            <div className="flex flex-col">
              <span className="font-medium">{option.label}</span>
            </div>
          </AutocompleteItem>
        ))}
      </Autocomplete>
      {selectedStatuses.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {selectedStatuses.map((statusKey) => (
            <Chip key={statusKey} size="sm" variant="flat" onClose={() => handleStatusRemove(statusKey)}>
              {getStatusLabel(statusKey)}
            </Chip>
          ))}
        </div>
      )}
    </div>
  );
}

export default FilterStatusMultiSelect;
